/**
 * Circle of the Land terrain types and their associated Circle Spells
 * SRD 5.1 Content
 */

export interface CircleSpellLevel {
  level: number; // Druid level when these spells are gained
  spells: string[]; // Spell IDs
}

export interface LandTerrain {
  id: string;
  name: string;
  description: string;
  circleSpells: CircleSpellLevel[];
}

/**
 * All Circle of the Land terrain types from SRD 5.1
 */
export const LAND_TERRAINS: LandTerrain[] = [
  {
    id: 'arctic',
    name: 'Arctic',
    description: 'Frozen tundras and icy wastelands where survival depends on endurance and cunning.',
    circleSpells: [
      { level: 3, spells: ['hold-person', 'spike-growth'] },
      { level: 5, spells: ['sleet-storm', 'slow'] },
      { level: 7, spells: ['freedom-of-movement', 'ice-storm'] },
      { level: 9, spells: ['commune-with-nature', 'cone-of-cold'] },
    ],
  },
  {
    id: 'coast',
    name: 'Coast',
    description: 'Sandy shores, rocky cliffs, and the boundary between land and sea.',
    circleSpells: [
      { level: 3, spells: ['mirror-image', 'misty-step'] },
      { level: 5, spells: ['water-breathing', 'water-walk'] },
      { level: 7, spells: ['control-water', 'freedom-of-movement'] },
      { level: 9, spells: ['conjure-elemental', 'scrying'] },
    ],
  },
  {
    id: 'desert',
    name: 'Desert',
    description: 'Arid lands of sand dunes, sun-baked rock, and rare oases.',
    circleSpells: [
      { level: 3, spells: ['blur', 'silence'] },
      { level: 5, spells: ['create-food-and-water', 'protection-from-energy'] },
      { level: 7, spells: ['blight', 'hallucinatory-terrain'] },
      { level: 9, spells: ['insect-plague', 'wall-of-stone'] },
    ],
  },
  {
    id: 'forest',
    name: 'Forest',
    description: 'Dense woodlands teeming with life, from towering trees to hidden glades.',
    circleSpells: [
      { level: 3, spells: ['barkskin', 'spider-climb'] },
      { level: 5, spells: ['call-lightning', 'plant-growth'] },
      { level: 7, spells: ['divination', 'freedom-of-movement'] },
      { level: 9, spells: ['commune-with-nature', 'tree-stride'] },
    ],
  },
  {
    id: 'grassland',
    name: 'Grassland',
    description: 'Rolling plains, savannas, and open prairies where the wind sweeps unimpeded.',
    circleSpells: [
      { level: 3, spells: ['invisibility', 'pass-without-trace'] },
      { level: 5, spells: ['daylight', 'haste'] },
      { level: 7, spells: ['divination', 'freedom-of-movement'] },
      { level: 9, spells: ['dream', 'insect-plague'] },
    ],
  },
  {
    id: 'mountain',
    name: 'Mountain',
    description: 'Towering peaks, rocky crags, and alpine meadows reaching toward the sky.',
    circleSpells: [
      { level: 3, spells: ['spider-climb', 'spike-growth'] },
      { level: 5, spells: ['lightning-bolt', 'meld-into-stone'] },
      { level: 7, spells: ['stone-shape', 'stoneskin'] },
      { level: 9, spells: ['passwall', 'wall-of-stone'] },
    ],
  },
  {
    id: 'swamp',
    name: 'Swamp',
    description: 'Murky wetlands, bogs, and marshes where decay feeds new life.',
    circleSpells: [
      { level: 3, spells: ['darkness', 'melfs-acid-arrow'] },
      { level: 5, spells: ['water-walk', 'stinking-cloud'] },
      { level: 7, spells: ['freedom-of-movement', 'locate-creature'] },
      { level: 9, spells: ['insect-plague', 'scrying'] },
    ],
  },
  {
    id: 'underdark',
    name: 'Underdark',
    description: 'The vast subterranean realm beneath the surface, filled with strange wonders and dangers.',
    circleSpells: [
      { level: 3, spells: ['spider-climb', 'web'] },
      { level: 5, spells: ['gaseous-form', 'stinking-cloud'] },
      { level: 7, spells: ['greater-invisibility', 'stone-shape'] },
      { level: 9, spells: ['cloudkill', 'insect-plague'] },
    ],
  },
];

/**
 * Get a land terrain by ID
 */
export function getLandTerrain(id: string): LandTerrain | undefined {
  return LAND_TERRAINS.find((t) => t.id === id);
}

/**
 * Get all land terrain IDs
 */
export function getLandTerrainIds(): string[] {
  return LAND_TERRAINS.map((t) => t.id);
}

/**
 * Get circle spells for a terrain at a specific druid level
 */
export function getCircleSpellsForLevel(terrainId: string, druidLevel: number): string[] {
  const terrain = getLandTerrain(terrainId);
  if (!terrain) return [];

  const spells: string[] = [];
  for (const spellLevel of terrain.circleSpells) {
    if (druidLevel >= spellLevel.level) {
      spells.push(...spellLevel.spells);
    }
  }
  return spells;
}
